import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useModels } from "../hooks/use-models.ts";
import { useRole } from "../hooks/use-role.ts";
import type {
  AppConfig,
  Persona,
  RunDiscoveryRunParams,
  RunDiscoveryRunResponse,
  Test,
} from "../types.ts";
import { TestSelect } from "./test-select.tsx";

type Props = {
  open: boolean;
  onClose: () => void;
  edgeCaseTarget?: string;
};

export const DiscoveryTriggerModal = ({ open, onClose, edgeCaseTarget }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const nav = useNavigate();

  const { role } = useRole();

  const { data: modelsData } = useModels();

  const [triggerInfo, setTriggerInfo] = useState<RunDiscoveryRunParams | null>(null);

  const [isMaxMode, setIsMaxMode] = useState(true);

  useEffect(() => {
    if (isMaxMode) {
      setTriggerInfo((prev) =>
        prev
          ? {
              ...prev,
              llm_model: modelsData?.defaults.max ?? "anthropic/claude-sonnet-4.5",
            }
          : null,
      );
    } else {
      setTriggerInfo((prev) =>
        prev
          ? {
              ...prev,
              llm_model: modelsData?.defaults.default ?? "@preset/gemini-flash",
            }
          : null,
      );
    }
  }, [isMaxMode, modelsData]);

  const { data: personas, isLoading: isLoadingPersonas } = useApiQuery<Persona[]>("/personas");

  const { data: appConfigs, isLoading: isLoadingAppConfigs } = useApiQuery<AppConfig[]>(
    "/vibe-qa/app-configs",
    {
      refetchInterval: 10_000,
    },
  );

  const nonLocalAppConfigs = useMemo(() => {
    return appConfigs?.filter((config) => config.cfg_type !== "local") || [];
  }, [appConfigs]);

  useEffect(() => {
    if (open) {
      setTriggerInfo({
        flow_count: 3, // Default value
        edge_case_target: edgeCaseTarget ?? undefined,
        variant: edgeCaseTarget ? "edge_case" : "discovery",
        app_config_id: nonLocalAppConfigs[0]?.id,
      });
    } else {
      setTriggerInfo(null);
    }
  }, [open, edgeCaseTarget, nonLocalAppConfigs]);

  const { data: edgeCaseSeed, isLoading: isLoadingEdgeCaseSeed } = useApiQuery<Test>(
    `/tests/${edgeCaseTarget}`,
    {
      enabled: !!edgeCaseTarget && open,
    },
  );

  useEffect(() => {
    if (!edgeCaseSeed || !!triggerInfo?.app_config_id) {
      return;
    }

    setTriggerInfo({
      ...triggerInfo,
      app_config_id: edgeCaseSeed.app_config_id,
      edge_case_target: edgeCaseSeed.id,
    });
  }, [triggerInfo, edgeCaseSeed?.app_config_id, edgeCaseSeed]);

  const isEdgeCase = useMemo(
    () => !!(edgeCaseTarget && edgeCaseSeed),
    [edgeCaseTarget, edgeCaseSeed],
  );

  const { mutate: newDiscoveryRun, isPending: isRunningNewDiscoveryRun } = useApiMutation<
    RunDiscoveryRunParams,
    RunDiscoveryRunResponse
  >("POST", `/discovery-runs/new`, {
    onSuccess: (resp) => {
      setTriggerInfo(null);

      enqueueSnackbar(
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="body1">New discovery run started successfully!</Typography>
          <IconButton
            aria-label="View discovery run"
            onClick={() => {
              nav(`/discovery-runs/${resp.agent_id}`);
            }}
            size="small"
            color="inherit"
          >
            <OpenInNewIcon htmlColor="white" />
          </IconButton>
        </Stack>,
        {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        },
      );
    },
    onError: (err) => {
      enqueueSnackbar(`Error starting new discovery run: ${err.message}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const closeTriggerDialog = () => {
    onClose();
    setTriggerInfo(null);
    setIsMaxMode(true);
  };

  const title = useMemo(() => {
    if (edgeCaseSeed) {
      return `Trigger edge case discovery`;
    }

    return "Trigger a new discovery";
  }, [edgeCaseSeed]);

  const params = new URLSearchParams(window.location.search);
  const debug = params.get("debug") === "true";

  return (
    <Dialog
      open={open}
      onClose={closeTriggerDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "1.1rem",
          fontWeight: 500,
          color: "text.primary",
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Stack direction="column" sx={{ pt: 2 }} spacing={2}>
          {edgeCaseSeed && (
            <Alert
              severity="info"
              sx={{
                borderRadius: 1.5,
                backgroundColor: "rgba(33, 150, 243, 0.08)",
                border: "1px solid rgba(33, 150, 243, 0.2)",
              }}
            >
              <Typography
                variant="body2"
                color="info.main"
                sx={{
                  fontSize: "0.875rem",
                  fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                }}
              >
                This discovery run will focus on finding edge cases based on the test{" "}
                <strong>{edgeCaseSeed.name}</strong>.
              </Typography>
            </Alert>
          )}

          <TextField
            id="intent"
            name="intent"
            label="Discovery Intent"
            placeholder="e.g., focus on UX, ensure login is covered, test checkout journey..."
            size="small"
            multiline
            minRows={2}
            maxRows={3}
            fullWidth
            value={triggerInfo?.intent || ""}
            onChange={(e) => {
              setTriggerInfo({
                ...triggerInfo,
                intent: e.target.value,
              });
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                fontSize: "0.875rem",
                fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                "&:hover fieldset": {
                  borderColor: "primary.main",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                  borderWidth: "1.5px",
                },
              },
              "& .MuiInputBase-input": {
                padding: "8px 12px",
                fontSize: "0.875rem",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.875rem",
                fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "0.75rem",
                fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
              },
            }}
          />

          {!isEdgeCase && (
            <TestSelect
              label={edgeCaseSeed ? "Edge Case Target" : "Starting Point"}
              placeholder={
                edgeCaseSeed
                  ? "Select the targeted edge case user journey..."
                  : "Select where to start the discovery from..."
              }
              value={triggerInfo?.edge_case_target ?? null}
              onChange={(testId) =>
                setTriggerInfo({
                  ...triggerInfo,
                  edge_case_target: testId ?? undefined,
                })
              }
              appConfigId={triggerInfo?.app_config_id}
              fullWidth
            />
          )}

          <Autocomplete
            loading={isLoadingPersonas}
            disabled={isLoadingPersonas}
            size="small"
            options={personas || []}
            value={personas?.find((p) => triggerInfo?.persona_id === p.id) || null}
            onChange={(_, value) => {
              setTriggerInfo((prev) =>
                prev
                  ? {
                      ...prev,
                      persona_id: value?.id,
                    }
                  : null,
              );
            }}
            renderOption={(props, option) => (
              <Box {...props} component="li">
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{
                    fontSize: "0.875rem",
                    fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                  }}
                >
                  {option.name}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select a persona to use in the discovery..."
                label="Persona"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    fontSize: "0.875rem",
                    fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                      borderWidth: "1.5px",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px 12px",
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                    fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.75rem",
                    fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                  },
                }}
              />
            )}
            getOptionLabel={(option) => option.name}
            autoComplete
            includeInputInList
            filterSelectedOptions
            clearOnBlur
            handleHomeEndKeys
            noOptionsText="No personas available"
            loadingText="Loading personas..."
            clearOnEscape={false}
            selectOnFocus={false}
            blurOnSelect={true}
            openOnFocus={true}
            sx={{
              "& .MuiAutocomplete-input": {
                cursor: "pointer",
              },
            }}
          />

          <Stack
            direction="row"
            spacing={2}
            sx={{ pt: 0 }}
            alignItems="flex-start"
            justifyContent="flex-start"
          >
            <Stack direction="column" spacing={0} sx={{ flex: 1 }}>
              <TextField
                id="flow-count"
                name="flow-count"
                label="Number of User Journeys"
                placeholder="3"
                size="small"
                type="number"
                fullWidth
                value={triggerInfo?.flow_count ?? 3}
                onChange={(e) => {
                  setTriggerInfo({
                    ...triggerInfo,
                    flow_count: parseInt(e.target.value, 10) || 3,
                  });
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    fontSize: "0.875rem",
                    fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                      borderWidth: "1.5px",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px 12px",
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                    fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.75rem",
                    fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                  },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  ml: 1,
                  display: "block",
                  fontSize: "0.75rem",
                  fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                }}
              >
                We recommend 3-5. (max: 50)
              </Typography>
            </Stack>

            <Stack direction="column" spacing={0} sx={{ flex: 1 }}>
              <Autocomplete
                loading={isLoadingAppConfigs}
                disabled={isLoadingAppConfigs}
                size="small"
                options={nonLocalAppConfigs}
                value={nonLocalAppConfigs.find((c) => triggerInfo?.app_config_id === c.id) || null}
                onChange={(_, value) => {
                  setTriggerInfo((prev) =>
                    prev
                      ? {
                          ...prev,
                          app_config_id: value?.id,
                        }
                      : null,
                  );
                }}
                renderOption={(props, option) => (
                  <Box {...props} component="li">
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{
                          fontSize: "0.875rem",
                          fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                        }}
                      >
                        {option.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: "0.75rem",
                          fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                        }}
                      >
                        {option.cfg_type}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select an app configuration..."
                    label="App Configuration"
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        fontSize: "0.875rem",
                        fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                          borderWidth: "1.5px",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: "8px 12px",
                        fontSize: "0.875rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: "0.75rem",
                        fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                      },
                    }}
                  />
                )}
                getOptionLabel={(option) => option.name}
                autoComplete
                includeInputInList
                filterSelectedOptions
                clearOnBlur
                handleHomeEndKeys
                noOptionsText="No app configurations available"
                loadingText="Loading app configurations..."
                clearOnEscape={false}
                selectOnFocus={false}
                blurOnSelect={true}
                openOnFocus={true}
                sx={{
                  "& .MuiAutocomplete-input": {
                    cursor: "pointer",
                  },
                }}
              />
            </Stack>

            <Box sx={{ display: "flex", alignItems: "center", pt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={isMaxMode}
                    onChange={(e) => setIsMaxMode(e.target.checked)}
                    sx={{
                      "& .MuiSwitch-thumb": {
                        backgroundColor: isMaxMode ? "info.dark" : "grey.500",
                      },
                      "& .MuiSwitch-track": {
                        backgroundColor: isMaxMode
                          ? "rgba(25, 118, 210, 0.3)"
                          : "rgba(158, 158, 158, 0.3)",
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                      }}
                    >
                      {isMaxMode ? "MAX Mode" : "Fast Mode"}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Stack>

          {debug && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={triggerInfo?.fast === true}
                  onChange={(e) =>
                    setTriggerInfo({
                      ...triggerInfo,
                      fast: e.target.checked,
                    })
                  }
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                  }}
                >
                  Fast Mode 🚀 {triggerInfo?.fast && " (beware that this may not be as accurate)"}
                </Typography>
              }
            />
          )}

          {debug && role === "admin" && (
            <Autocomplete
              disabled={role !== "admin"}
              size="small"
              options={modelsData?.models ?? []}
              value={
                (modelsData?.models ?? []).find((m) => triggerInfo?.llm_model === m.id) || null
              }
              onChange={(_, value) => {
                setTriggerInfo((prev) =>
                  prev
                    ? {
                        ...prev,
                        llm_model: value?.id,
                      }
                    : null,
                );
              }}
              renderOption={(props, option) => (
                <Box {...props} component="li">
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{
                      fontSize: "0.875rem",
                      fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                    }}
                  >
                    {option.name}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select which LLM to use for the Discovery Run"
                  label="(Admin) LLM to use"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      fontSize: "0.875rem",
                      fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "primary.main",
                        borderWidth: "1.5px",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: "8px 12px",
                      fontSize: "0.875rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                      fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                    },
                    "& .MuiFormHelperText-root": {
                      fontSize: "0.75rem",
                      fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                    },
                  }}
                />
              )}
              getOptionLabel={(option) => option.name}
              autoComplete
              includeInputInList
              filterSelectedOptions
              clearOnBlur
              handleHomeEndKeys
              noOptionsText="No LLMs available"
              loadingText="Loading LLMs..."
              clearOnEscape={false}
              selectOnFocus={false}
              blurOnSelect={true}
              openOnFocus={true}
              sx={{
                "& .MuiAutocomplete-input": {
                  cursor: "pointer",
                },
              }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0, justifyContent: "right" }}>
        <Button
          onClick={() => {
            if (triggerInfo) {
              newDiscoveryRun(triggerInfo);
            }
            closeTriggerDialog();
          }}
          disabled={
            isRunningNewDiscoveryRun || !triggerInfo || (!!edgeCaseTarget && isLoadingEdgeCaseSeed)
          }
          loading={isRunningNewDiscoveryRun || isLoadingEdgeCaseSeed}
          variant="contained"
          sx={{
            borderRadius: 1.5,
            textTransform: "none",
            fontWeight: 500,
            px: 1,
            py: 0.5,
            fontSize: "0.875rem",
            fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
            minWidth: 80,
          }}
        >
          Start
        </Button>
      </DialogActions>
    </Dialog>
  );
};
